import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Loader2, RefreshCw, Plus, Trash2, Save, Sparkles, AlertCircle } from 'lucide-react';
import { organisationsAPI } from '../../services/api';
import toast from '../../services/toast';
import { useConfirm } from '../../contexts/ConfirmContext';
import { CardSkeleton } from '../../components/ui/Skeleton';
import { EmptyState } from '../../components/ui/PageLayout';
import FormField from '../../components/forms/FormField';
import SkillTag from '../../components/skills/SkillTag';
import {
  groupTagsByType,
  tagTypeLabel,
  DECLARATION_LEVELS,
  DECLARABLE_TAG_TYPES,
  PROFICIENCY_LABELS,
} from '../../components/skills/skillsConstants';

const EMPTY_DECLARATION = {
  tagType: 'tech',
  name: '',
  proficiencyLevel: '',
  yearsExperience: '',
  notes: '',
};

const STATUS_LABEL = {
  pending: 'Pending generation',
  running: 'Generating…',
  success: 'Up to date',
  failed: 'Generation failed',
};

/**
 * Skills tab for the member detail page. Read-only for other members; full
 * self-service editing (summary, tag review, declarations) when viewing your own profile.
 */
export default function MemberSkillsSection({ orgId, memberId, isOwnProfile }: any) {
  const { confirm } = useConfirm();
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [reviewingTagId, setReviewingTagId] = useState<any>(null);
  const [declarations, setDeclarations] = useState<any[]>([]);
  const [expandedTagTypes, setExpandedTagTypes] = useState(() => new Set());

  const {
    register: registerSummary,
    handleSubmit: handleSummarySubmit,
    reset: resetSummary,
    watch: watchSummary,
    formState: { isSubmitting: savingSummary },
  } = useForm({ defaultValues: { summaryDraft: '' } });
  const summaryDraft = watchSummary('summaryDraft');

  const {
    register: registerDecl,
    handleSubmit: handleDeclSubmit,
    reset: resetDecl,
    formState: { isSubmitting: savingDecl },
  } = useForm({ defaultValues: EMPTY_DECLARATION });

  const TAGS_PREVIEW_COUNT = 8;

  const loadProfile = useCallback(async () => {
    if (!orgId || !memberId) return;
    try {
      const p = await organisationsAPI.getSkillProfile(orgId, memberId);
      setProfile(p);
      resetSummary({ summaryDraft: p?.userSummary || '' });
    } catch (err) {
      if ((err as any)?.status === 404) {
        setProfile(null);
      } else {
        toast.error((err as Error).message || 'Failed to load skill profile');
      }
    }
  }, [orgId, memberId, resetSummary]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadProfile().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [loadProfile]);

  useEffect(() => {
    if (!isOwnProfile || !orgId) return;
    organisationsAPI
      .listMySkillDeclarations(orgId)
      .then((list) => setDeclarations(Array.isArray(list) ? list : []))
      .catch(() => setDeclarations([]));
  }, [isOwnProfile, orgId]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await organisationsAPI.generateSkillProfile(orgId, memberId, { force: true });
      toast.success('Profile generation queued — refresh in a moment to see updates.');
      await loadProfile();
    } catch (err) {
      toast.error((err as Error).message || 'Failed to queue generation');
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveSummary = handleSummarySubmit(async ({ summaryDraft: draft }) => {
    try {
      const updated = await organisationsAPI.updateMySkillSummary(orgId, draft);
      setProfile(updated);
      resetSummary({ summaryDraft: updated?.userSummary || draft });
      toast.success('Summary saved');
    } catch (err) {
      toast.error((err as Error).message || 'Failed to save summary');
    }
  });

  const handleReviewTag = async (tag, review) => {
    setReviewingTagId(tag.id);
    try {
      // `null` review toggles a confirmed tag back to unreviewed.
      const value = review === null ? 'adjusted' : review;
      const updated = await organisationsAPI.reviewMySkillTag(orgId, tag.id, value);
      setProfile((prev) =>
        prev
          ? {
              ...prev,
              tags: (prev.tags || []).map((t) =>
                t.id === tag.id ? { ...t, userReview: updated?.userReview ?? value } : t
              ),
            }
          : prev
      );
    } catch (err) {
      toast.error((err as Error).message || 'Failed to update skill');
    } finally {
      setReviewingTagId(null);
    }
  };

  const handleSaveDeclaration = handleDeclSubmit(async (values) => {
    const name = values.name.trim();
    if (!name) {
      toast.error('Skill name is required');
      return;
    }
    try {
      const payload = {
        tagType: values.tagType,
        name,
        proficiencyLevel: values.proficiencyLevel || undefined,
        yearsExperience: values.yearsExperience !== '' ? Number(values.yearsExperience) : undefined,
        notes: values.notes.trim() || undefined,
      };
      const saved = await organisationsAPI.upsertMySkillDeclaration(orgId, payload);
      setDeclarations((prev) => {
        const others = prev.filter((d) => !(d.tagType === saved.tagType && d.name === saved.name));
        return [...others, saved].sort(
          (a, b) => a.tagType.localeCompare(b.tagType) || a.name.localeCompare(b.name)
        );
      });
      resetDecl(EMPTY_DECLARATION);
      toast.success('Skill declared — your profile will refresh shortly.');
    } catch (err) {
      toast.error((err as Error).message || 'Failed to save declaration');
    }
  });

  const handleDeleteDeclaration = async (decl) => {
    const ok = await confirm({
      title: 'Remove declared skill',
      message: `Remove "${decl.name}" from your declared skills?`,
      confirmLabel: 'Remove',
      cancelLabel: 'Cancel',
    });
    if (!ok) return;
    try {
      await organisationsAPI.deleteMySkillDeclaration(orgId, decl.id);
      setDeclarations((prev) => prev.filter((d) => d.id !== decl.id));
      toast.success('Skill removed');
    } catch (err) {
      toast.error((err as Error).message || 'Failed to remove skill');
    }
  };

  const tagGroups = useMemo(() => groupTagsByType(profile?.tags || []), [profile]);

  const toggleTagGroupExpanded = (tagType) => {
    setExpandedTagTypes((prev) => {
      const next = new Set(prev);
      if (next.has(tagType)) next.delete(tagType);
      else next.add(tagType);
      return next;
    });
  };

  const sortedTagsForGroup = (tags) =>
    [...tags].sort(
      (a, b) => (b.evidenceScore ?? 0) - (a.evidenceScore ?? 0) || a.name.localeCompare(b.name)
    );
  const summary = profile?.userSummary || profile?.generatedSummary;

  if (loading) return <CardSkeleton />;

  if (!profile) {
    return (
      <EmptyState
        icon={<Sparkles className="w-10 h-10 text-slate-500" />}
        title="No skill profile yet"
        description={
          isOwnProfile
            ? 'Your skill profile is built from your GitHub activity. Generate it now, or add declarations below.'
            : "This member's skill profile has not been generated yet."
        }
        action={
          <button
            type="button"
            onClick={handleGenerate}
            disabled={generating}
            className="btn-primary-mdl inline-flex items-center gap-2"
          >
            {generating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            Generate profile
          </button>
        }
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${
              profile.generationStatus === 'success'
                ? 'bg-vivid-green-cyan/15 text-vivid-green-cyan'
                : profile.generationStatus === 'failed'
                  ? 'bg-vivid-red/15 text-vivid-red'
                  : 'bg-slate-500/20 text-slate-300'
            }`}
          >
            {profile.generationStatus === 'running' && <Loader2 className="w-3 h-3 animate-spin" />}
            {STATUS_LABEL[profile.generationStatus] || profile.generationStatus}
          </span>
          {profile.generatedAt && (
            <span className="text-xs">
              Generated {new Date(profile.generatedAt).toLocaleDateString()}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={handleGenerate}
          disabled={generating}
          className="btn-secondary-mdl inline-flex items-center gap-2 text-sm"
        >
          {generating ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4" />
          )}
          Regenerate
        </button>
      </div>

      {profile.generationStatus === 'failed' && profile.generationError && (
        <div
          className="flex items-start gap-2 rounded-lg border border-vivid-red/40 bg-vivid-red/10 px-3 py-2 text-sm text-vivid-red"
          role="alert"
        >
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden />
          <span>{profile.generationError}</span>
        </div>
      )}

      {/* Summary */}
      <section>
        <h3 className="text-sm font-semibold text-slate-200 mb-2">Summary</h3>
        {isOwnProfile ? (
          <div className="space-y-2">
            {profile.generatedSummary && !profile.userSummary && (
              <p className="text-sm text-slate-400 italic flex items-start gap-1.5">
                <Sparkles className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-vivid-red" aria-hidden />
                AI-generated: {profile.generatedSummary}
              </p>
            )}
            <textarea
              className="input-mdl w-full min-h-[90px] resize-y"
              placeholder="Write a short summary of your skills and focus areas (overrides the AI summary)…"
              maxLength={4000}
              {...registerSummary('summaryDraft')}
            />
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleSaveSummary}
                disabled={savingSummary || summaryDraft === (profile.userSummary || '')}
                className="btn-primary-mdl inline-flex items-center gap-2 text-sm disabled:opacity-50"
              >
                {savingSummary ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Save summary
              </button>
            </div>
          </div>
        ) : summary ? (
          <p className="text-sm text-slate-300">{summary}</p>
        ) : (
          <p className="text-sm text-slate-500 italic">No summary available.</p>
        )}
      </section>

      {/* Derived + declared skills */}
      <section>
        <h3 className="text-sm font-semibold text-slate-200 mb-2">Skills</h3>
        {isOwnProfile && (
          <p className="text-xs text-slate-400 mb-3">
            These are derived from your GitHub activity. Confirm the ones that are accurate or
            reject any that don&apos;t belong — your feedback refines future generations.
          </p>
        )}
        {tagGroups.length === 0 ? (
          <p className="text-sm text-slate-500 italic">No skills derived yet.</p>
        ) : (
          <div className="space-y-3">
            {tagGroups.map(({ tagType, tags }) => {
              const sorted = sortedTagsForGroup(tags);
              const expanded = expandedTagTypes.has(tagType);
              const visible = expanded ? sorted : sorted.slice(0, TAGS_PREVIEW_COUNT);
              const hiddenCount = sorted.length - TAGS_PREVIEW_COUNT;
              return (
                <div key={tagType}>
                  <p className="text-xs font-medium text-slate-500 mb-1.5">
                    {tagTypeLabel(tagType)}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {visible.map((tag) => (
                      <SkillTag
                        key={tag.id}
                        tag={tag}
                        onReview={isOwnProfile ? handleReviewTag : undefined}
                        reviewing={reviewingTagId === tag.id}
                      />
                    ))}
                  </div>
                  {hiddenCount > 0 && (
                    <button
                      type="button"
                      onClick={() => toggleTagGroupExpanded(tagType)}
                      className="mt-1.5 text-xs text-slate-400 hover:text-slate-200 underline-offset-2 hover:underline"
                    >
                      {expanded ? 'Show less' : `Show all (${sorted.length})`}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Self-declarations (own profile only) */}
      {isOwnProfile && (
        <section>
          <h3 className="text-sm font-semibold text-slate-200 mb-2">Declared skills</h3>
          <p className="text-xs text-slate-400 mb-3">
            Add skills that may not show up in your commit history — languages, domains, or tools
            you know but haven&apos;t recently committed.
          </p>

          {declarations.length > 0 && (
            <ul className="mb-4 divide-y divide-dark-border rounded-xl border border-dark-border overflow-hidden">
              {declarations.map((d) => (
                <li key={d.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                  <span className="font-medium text-slate-200">{d.name}</span>
                  <span className="text-xs text-slate-500">{tagTypeLabel(d.tagType)}</span>
                  {d.proficiencyLevel && (
                    <span className="text-xs rounded bg-dark-elevated px-1.5 py-0.5 text-slate-300">
                      {PROFICIENCY_LABELS[d.proficiencyLevel] || d.proficiencyLevel}
                    </span>
                  )}
                  {d.yearsExperience != null && (
                    <span className="text-xs text-slate-400">{d.yearsExperience} yr</span>
                  )}
                  <button
                    type="button"
                    onClick={() => handleDeleteDeclaration(d)}
                    className="ml-auto rounded p-1 text-slate-400 hover:bg-vivid-red/15 hover:text-vivid-red"
                    aria-label={`Remove ${d.name}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <form
            onSubmit={handleSaveDeclaration}
            className="rounded-xl border border-dark-border bg-dark-surface/50 p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"
            noValidate
          >
            <FormField id="decl-name" label="Skill name" className="lg:col-span-2">
              <input
                id="decl-name"
                type="text"
                className="input-mdl w-full"
                placeholder="e.g. Kubernetes"
                {...registerDecl('name', { required: true })}
              />
            </FormField>
            <FormField id="decl-type" label="Type">
              <select id="decl-type" className="input-mdl w-full" {...registerDecl('tagType')}>
                {DECLARABLE_TAG_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {tagTypeLabel(t)}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField id="decl-level" label="Proficiency">
              <select
                id="decl-level"
                className="input-mdl w-full"
                {...registerDecl('proficiencyLevel')}
              >
                <option value="">Unspecified</option>
                {DECLARATION_LEVELS.map((l) => (
                  <option key={l} value={l}>
                    {PROFICIENCY_LABELS[l]}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField id="decl-years" label="Years (optional)">
              <input
                id="decl-years"
                type="number"
                min="0"
                max="80"
                step="0.5"
                className="input-mdl w-full"
                {...registerDecl('yearsExperience')}
              />
            </FormField>
            <FormField id="decl-notes" label="Notes (optional)" className="lg:col-span-3">
              <input
                id="decl-notes"
                type="text"
                className="input-mdl w-full"
                placeholder="Context, certifications, etc."
                {...registerDecl('notes')}
              />
            </FormField>
            <div className="flex items-end justify-end">
              <button
                type="submit"
                disabled={savingDecl}
                className="btn-primary-mdl inline-flex items-center gap-2 text-sm w-full sm:w-auto justify-center"
              >
                {savingDecl ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
                Add skill
              </button>
            </div>
          </form>
        </section>
      )}
    </div>
  );
}
